import React, {
  useState,
} from 'react';
import i18n from 'i18next';
import {
  useHistory,
} from 'react-router-dom';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';

import {
  Modal,
  Button,
  AddEntity,
  AddClient,
  FileUploader,
  ConfirmModal,
  MoveToFolder,
  SearchVideoModal,
} from 'web-components';
import {
  Flex,
  Text,
} from 'web-styled';

const mapState = state => ({
  settings: coreSelectors.getProfile('user_settings')(state),
  clientCountAllowed: coreSelectors.getClientCountAllowed(state),
  ...getUiState([
    'fileUpload',
    'confirmModal',
    'addClientModal',
    'addEntityModal',
    'moveEntityModal',
    'uploadVideoModal',
  ])(state),
});

const Modals = () => {
  const {
    settings,
    fileUpload,
    confirmModal,
    addEntityModal,
    addClientModal,
    uploadVideoModal,
    moveEntityModal,
    clientCountAllowed,
  } = useSelector(mapState);

  const history = useHistory();
  const dispatch = useDispatch();
  const [emailExists, setEmailExists] = useState<boolean>(false);

  const onSuccessAddClient = () => {
    dispatch(
      uiActions.setUiState({
        addClientModal: false,
      }),
    );
    if (!settings.tutorial_steps.includes('add_client')) {
      dispatch(
        coreActions.requestUpdateSettings({
          body: {
            tutorial_steps: Array.from(
              new Set([...settings.tutorial_steps, 'add_client']),
            ),
          },
          settingsId: settings.id,
        }),
      );
    }
  };

  return (
    <>
      <SearchVideoModal
        onRequestClose={() => {
          dispatch(
            uiActions.cancelSearchVideoModal(),
          );
        }}
      />
      <FileUploader
        accept="video/*"
        title="Upload Video"
        isOpen={uploadVideoModal.isOpen}
        multiple={uploadVideoModal.multiple}
        isLoaded={uploadVideoModal.isLoaded}
        placeholder="Drop your media files here or"
        resourceType="videos"
        fileUpload={fileUpload}
        onRequestDrop={(files) => {
          dispatch(
            uiActions.requestUploadVideos(files),
          );
        }}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              fileUpload: {},
              uploadVideoModal: {
                isOpen: false,
              },
            }),
          );
        }}
        onRequestCancel={() => {
          dispatch(uiActions.cancelUploadFiles());
        }}
      />
      <ConfirmModal
        modal={confirmModal}
        onRequestClose={() => {
          dispatch(
            uiActions.cancelConfirmModal(),
          );
        }}
        onRequestConfirm={(_, actions) => {
          const close = () => {
            dispatch(
              uiActions.setUiState({
                confirmModal: {
                  isOpen: false,
                },
              }),
            );
          };
          dispatch(
            uiActions.okConfirmModal(
              actions,
              close,
            ),
          );
        }}
      />
      <AddClient
        isOpen={addClientModal}
        isLimitReached={clientCountAllowed < 1}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              addClientModal: false,
            }),
          );
        }}
        onSubmit={(values, actions) => {
          const {
            email,
            ...rest
          } = values;
          const add = (payload) => {
            dispatch(
              coreActions.requestAddClientEntity({
                list: 'clientList',
                payload,
                resourceType: 'clients',
                callbacks: {
                  ...actions,
                  onSuccess: onSuccessAddClient,
                },
              }),
            );
          };

          if (email) {
            dispatch(
              coreActions.checkEmail({
                email,
                callbacks: {
                  onError: () => {
                    setEmailExists(true);
                    actions.setSubmitting(false);
                  },
                  onSuccess: () => {
                    add({
                      email,
                      ...rest,
                    });
                  },
                },
              }),
            );
          } else {
            add(rest);
          }
        }}
      />
      <Modal
        isOpen={emailExists}
        onRequestClose={() => {
          setEmailExists(false);
        }}
      >
        <Flex
          column
          width="100%"
          align="center"
          spacing={10}
        >
          <Text>
            Email already exists in GymCloud database.
            Please contact customer support.
          </Text>
          <Button
            ghost
            appearance="primary"
            onClick={() => {
              setEmailExists(false);
            }}
          >
            Ok
          </Button>
        </Flex>
      </Modal>

      <AddEntity
        isOpen={addEntityModal.isOpen}
        label={i18n.t(`add.${addEntityModal.resourceType}`)}
        buttonLabel={addEntityModal.buttonLabel || 'Save'}
        folderName={addEntityModal.folderName}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              addEntityModal: {
                isOpen: false,
              },
            }),
          );
        }}
        onSubmit={(values, actions) => {
          const onSuccess = (entity) => {
            dispatch(
              uiActions.setUiState({
                addEntityModal: {
                  isOpen: false,
                },
              }),
            );

            let url = '';
            switch (addEntityModal.resourceType) {
              case 'exercises':
                url = `/exercises/${entity.id}/edit`;
                break;
              case 'workoutTemplates':
                url = `/workout_templates/${entity.id}/workout`;
                break;
              case 'warmups':
                url = `/warmups/${entity.id}/workout`;
                break;
              case 'cooldowns':
                url = `/cooldowns/${entity.id}/workout`;
                break;
              case 'programTemplates':
                url = `/program_templates/${entity.id}/workouts`;
                break;
              default:
                url = '/welcome';
            }
            history.push(url);
          };

          const payload = addEntityModal.meta
            ? {
              ...addEntityModal.meta,
              ...values,
            } : values;

          dispatch(
            coreActions.requestAddEntity({
              payload,
              list: 'library',
              resourceType: addEntityModal.resourceType,
              callbacks: {
                ...actions,
                onSuccess,
              },
            }),
          );
        }}
      />

      <MoveToFolder
        modalState={moveEntityModal}
        onSubmit={(values, actions) => {
          dispatch(
            uiActions.selectMoveEntityModal(values, actions),
          );
        }}
        onRequestClose={() => {
          dispatch(
            uiActions.calcelMoveEntityModal(),
          );
        }}
      />
    </>
  );
};

export default Modals;
